// ✅ Trekkin.app — PanelLogin.jsx v6.1.0 (FINAL)
// -------------------------------------------------------------
// 🔹 LOGIN + REGISTO + RECUPERAÇÃO DE PASSWORD
// 🔹 Compatível com useAuthStore.jsx v6.5.0 atual (Regra de Ouro)
// 🔹 username → enviado 100% correto ao register()
// 🔹 Botão "Esqueci-me da palavra-passe" → abre resetPassword
// 🔹 Mensagens claras e UX idêntica ao original
// -------------------------------------------------------------

import { useState } from "react";
import { useAuthStore } from "../stores/useAuthStore";
import { usePanelsStore } from "../stores/usePanelsStore";
import { useTranslate } from "../i18n/useTranslate";
import { Capacitor } from "@capacitor/core";

const base = import.meta.env.BASE_URL || "/";
const logoSrc = `${base}logo-trekkin.png`;
const appStoreUrl = "https://apps.apple.com/us/app/trekkin-pt/id6756221781";
const playStoreUrl =
  "https://play.google.com/store/apps/details?id=com.trekkin.app&pcampaignid=web_share";
const googleLogoSrc = `${base}icons/google_logo.svg`;
const appleLogoSrc = `${base}icons/apple_logo.svg`;

// -------------------------------------------------------------
// Helper → detetar erro "email já existe"
// -------------------------------------------------------------
function isEmailAlreadyExists(err) {
  if (!err) return false;

  let raw =
    typeof err === "string"
      ? err
      : err.code || err.message || JSON.stringify(err);

  const lower = raw.toLowerCase();
  return (
    lower.includes("email-already-in-use") ||
    lower.includes("email_exists") ||
    lower.includes("email já existe") ||
    lower.includes("email ja existe")
  );
}

// -------------------------------------------------------------
// COMPONENTE
// -------------------------------------------------------------
export default function PanelLogin() {
  // store está FINAL v6.5.0 → register() já recebe (email, pass, fn, ln, username)
  const { login, register, loginWithGoogle, loginWithApple } = useAuthStore();
  const { setActivePanel } = usePanelsStore();
  const translate = useTranslate();
  const platform = (() => {
    try {
      const isNative = Capacitor?.isNativePlatform?.() === true;
      if (isNative) {
        const p = Capacitor?.getPlatform?.();
        if (p === "ios" || p === "android") return p;
        return "native";
      }
      const p = window?.Capacitor?.getPlatform?.();
      if (p === "ios" || p === "android") return p;
    } catch {}
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const isiOSLike =
      /iPad|iPhone|iPod/i.test(ua) ||
      (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        Number(navigator.maxTouchPoints || 0) > 1);
    if (isiOSLike) {
      return "ios";
    }
    if (/Android/i.test(ua)) {
      return "android";
    }
    return "web";
  })();
  const showStoreDownloads = platform === "web";
  const showAppStore = showStoreDownloads;
  const showPlayStore = showStoreDownloads;
  const showGoogleLogin = platform !== "ios";
  const showAppleLogin = platform !== "android";
  const showAnySocial = showGoogleLogin || showAppleLogin;

  // estado base
  const [isRegistering, setIsRegistering] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // -------------------------------------------------------------
  // SUBMIT
  // -------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setMsg("");

    const uname = username.trim().toLowerCase();

    // ============================================================
    //                REGISTO DE UTILIZADOR
    // ============================================================
    if (isRegistering) {
      if (
        !email ||
        !password ||
        !confirmPassword ||
        !firstName ||
        !lastName ||
        !username
      ) {
        setMsg(translate("login.msg.fillAll"));
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setMsg(translate("login.msg.passwordMismatch"));
        setLoading(false);
        return;
      }

      if (!/^[a-z0-9_]{3,20}$/.test(uname)) {
        setMsg(translate("login.msg.usernameInvalid"));
        setLoading(false);
        return;
      }
      if (!acceptedTerms) {
        setMsg(translate("login.msg.acceptTerms"));
        setLoading(false);
        return;
      }

      try {
        const result = await register(
          email.trim(),
          password,
          firstName.trim(),
          lastName.trim(),
          uname
        );

        const ok = result === true || result?.ok === true;

        if (ok) {
          setMsg(translate("login.msg.registerSuccess"));
          window.dispatchEvent(new CustomEvent("trekkin:close-login"));
        } else if (isEmailAlreadyExists(result)) {
          setMsg(translate("login.msg.emailExists"));
        } else {
          setMsg(translate("login.msg.registerFail"));
        }
      } catch (err) {
        if (isEmailAlreadyExists(err)) {
          setMsg(translate("login.msg.emailExists"));
        } else {
          setMsg(translate("login.msg.registerUnexpected"));
        }
      }

      setLoading(false);
      return;
    }

    // ============================================================
    //                       LOGIN
    // ============================================================
    try {
      const result = await login(email.trim(), password);
      if (result === true) {
        setMsg(translate("login.msg.loginSuccess"));
        window.dispatchEvent(new CustomEvent("trekkin:close-login"));
      } else if (result?.error) {
        setMsg(String(result.error));
      } else {
        setMsg(translate("login.msg.invalidCredentials"));
      }
    } catch (err) {
      setMsg(translate("login.msg.loginError"));
    }

    setLoading(false);
  }

  async function handleSocialLogin(provider) {
    setLoading(true);
    setMsg("");
    try {
      const result =
        provider === "google" ? await loginWithGoogle() : await loginWithApple();

      if (result === true) {
        setMsg("Login social concluído.");
        window.dispatchEvent(new CustomEvent("trekkin:close-login"));
      } else if (result?.pendingRedirect) {
        setMsg("A concluir autenticação...");
      } else if (result?.error) {
        setMsg(String(result.error));
      } else {
        setMsg("Não foi possível concluir o login social.");
      }
    } catch (err) {
      setMsg("Erro no login social.");
    } finally {
      setLoading(false);
    }
  }

  // -------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------
  return (
    <div style={wrapper}>
      <form onSubmit={handleSubmit} style={form}>
        {/* LOGO + TÍTULO */}
        <div style={{ textAlign: "center", marginBottom: 6 }}>
          <img src={logoSrc} alt="Trekkin" style={logo} />
          <h2 style={title}>
            {isRegistering
              ? translate("login.title.register")
              : translate("login.title.login")}
          </h2>
        </div>

        {/* EMAIL */}
        <input
          type="email"
          placeholder={translate("login.placeholder.email")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          style={input}
        />

        {/* PASSWORD */}
        <input
          type="password"
          placeholder={translate("login.placeholder.password")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={input}
        />

        {/* CAMPOS EXTRA — REGISTO */}
        {isRegistering && (
          <>
            <input
              type="password"
              placeholder={translate("login.placeholder.confirmPassword")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              style={input}
            />

            <input
              type="text"
              placeholder={translate("login.placeholder.firstName")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              style={input}
            />

            <input
              type="text"
              placeholder={translate("login.placeholder.lastName")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              style={input}
            />

            <input
              type="text"
              placeholder={translate("login.placeholder.username")}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={input}
            />
          </>
        )}

        {isRegistering && (
        <label
          style={{
            fontSize: 12,
            color: "#374151",
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginTop: 4,
            lineHeight: 1.4,
          }}
        >
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>
            {translate("login.terms.prefix")}{" "}
            <a
              href="https://www.trekkin.pt/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", textDecoration: "underline" }}
            >
              {translate("login.terms.linkText")}
            </a>
            {translate("login.terms.suffix")}
          </span>
        </label>
      )}

        {/* BOTÃO PRINCIPAL */}
        <button
        type="submit"
        disabled={loading || (isRegistering && !acceptedTerms)}
        style={{
          ...button,
          background: "#28a745",
          opacity:
            loading || (isRegistering && !acceptedTerms) ? 0.6 : 1,
          cursor:
            loading || (isRegistering && !acceptedTerms)
              ? "not-allowed"
              : "pointer",
        }}
      >
          {loading
            ? translate("login.button.wait")
            : isRegistering
            ? translate("login.button.register")
            : translate("login.button.login")}
        </button>

        {/* LOGIN ↔ REGISTO */}
        <p
          style={toggle}
          onClick={() => {
            setIsRegistering(!isRegistering);
            setMsg("");
            setConfirmPassword("");
          }}
        >
          {isRegistering
            ? translate("login.toggle.toLogin")
            : translate("login.toggle.toRegister")}
        </p>

        {/* ESQUECI-ME DA PASSWORD */}
        {!isRegistering && (
          <p
            style={forgot}
            onClick={() => setActivePanel("resetPassword")}
          >
            {translate("login.forgot")}
          </p>
        )}

        {!isRegistering && showAnySocial && (
          <>
            <div style={socialDividerWrap}>
              <span style={socialDividerLine} />
              <span style={socialDividerText}>ou</span>
              <span style={socialDividerLine} />
            </div>

            {showGoogleLogin && (
              <button
                type="button"
                onClick={() => handleSocialLogin("google")}
                disabled={loading}
                style={socialBtn}
              >
                <img src={googleLogoSrc} alt="Google" style={socialLogo} />
                <span>Entrar com Google</span>
              </button>
            )}

            {showAppleLogin && (
              <button
                type="button"
                onClick={() => handleSocialLogin("apple")}
                disabled={loading}
                style={socialBtn}
              >
                <img src={appleLogoSrc} alt="Apple" style={socialLogo} />
                <span>Entrar com Apple</span>
              </button>
            )}
          </>
        )}

        {/* MENSAGENS */}
        {msg && (
          <p
            style={{
              textAlign: "center",
              fontSize: 14,
              marginTop: 4,
              color:
                msg.startsWith("✅") || msg.startsWith("📧")
                  ? "#15803d"
                  : "#b91c1c",
            }}
          >
            {msg}
          </p>
        )}

        {showStoreDownloads && (
          <>
            <p style={downloadHint}>
              Para tua segurança, descarrega a Trekkin.pt apenas na loja oficial do teu dispositivo.
            </p>

            <div style={downloadWrap}>
              {showAppStore && (
                <a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={downloadButton}
                >
                  <img
                    src={`${base}icons/download_android.svg`}
                    alt="Download iOS"
                    style={downloadIcon}
                  />
                </a>
              )}

              {showPlayStore && (
                <a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={downloadButton}
                >
                  <img
                    src={`${base}icons/download_apple.svg`}
                    alt="Download Android"
                    style={downloadIcon}
                  />
                </a>
              )}
            </div>
          </>
        )}
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// ESTILOS
// -------------------------------------------------------------
const wrapper = {
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  height: "100dvh",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  boxSizing: "border-box",
  padding:
    "max(10px, env(safe-area-inset-top, 0px)) 10px calc(40px + env(safe-area-inset-bottom, 0px))",
};

const form = {
  background: "rgba(255,255,255,0.96)",
  borderRadius: 12,
  padding: "18px 18px 20px",
  boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  width: "min(420px, calc(100vw - 32px))",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const input = {
  padding: "0.55rem 0.9rem",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 15,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

const button = {
  padding: "0.7rem 1rem",
  borderRadius: 8,
  border: "none",
  color: "#fff",
  fontWeight: 600,
  fontSize: 15,
  cursor: "pointer",
};

const toggle = {
  textAlign: "center",
  fontSize: 13,
  color: "#2563eb",
  cursor: "pointer",
  textDecoration: "underline",
  overflowWrap: "anywhere",
};

const forgot = {
  textAlign: "center",
  fontSize: 13,
  color: "#374151",
  cursor: "pointer",
  marginTop: -4,
  textDecoration: "underline",
  overflowWrap: "anywhere",
};

const logo = {
  width: 120,
  marginBottom: 8,
  filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.35)) brightness(1.02)",
};

const title = {
  margin: 0,
  fontSize: 20,
  color: "#111827",
  fontWeight: 700,
};

const downloadWrap = {
  marginTop: 6,
  display: "flex",
  width: "100%",
  gap: 10,
  padding: "0 4px",
  boxSizing: "border-box",
};

const downloadButton = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textDecoration: "none",
  padding: 0,
  minWidth: 0,
};

const downloadIcon = {
  width: "100%",
  height: "auto",
  objectFit: "contain",
  display: "block",
};

const downloadHint = {
  margin: "2px 0 0",
  textAlign: "center",
  fontSize: 12,
  color: "#4b5563",
  lineHeight: 1.35,
};

const socialDividerWrap = {
  marginTop: 2,
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const socialDividerLine = {
  flex: 1,
  height: 1,
  background: "#d1d5db",
};

const socialDividerText = {
  fontSize: 13,
  color: "#6b7280",
  textTransform: "lowercase",
};

const socialBtn = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  width: "100%",
  padding: "0.7rem 1rem",
  borderRadius: 8,
  border: "1px solid #86c5a0",
  background: "#f2fbf5",
  color: "#14532d",
  fontWeight: 700,
  fontSize: 15,
  cursor: "pointer",
};

const socialLogo = {
  width: 22,
  height: 22,
  objectFit: "contain",
  display: "block",
};
